import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, authHeaders } from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import { Panel, SectionLabel } from "@/components/admin/cc";
import { SITE } from "@/constants/vhm";
import { CheckCircle2, AlertTriangle, Mail, Database, Globe, HardDrive, Users } from "lucide-react";

const StatusPill = ({ ok, okText = "Actief", offText = "Niet geconfigureerd" }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-body text-[11px] font-bold ${ok ? "bg-cc-pos/10 text-cc-pos" : "bg-cc-warn/10 text-cc-warn"}`}>
    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
    {ok ? okText : offText}
  </span>
);

const StatusCard = ({ icon: Icon, title, children, ok, testid }) => (
  <Panel className="p-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-cc-accent/10 text-cc-accent"><Icon className="w-5 h-5" /></span>
        <h3 className="font-heading font-bold text-base text-cc-ink">{title}</h3>
      </div>
      {ok !== undefined && <StatusPill ok={ok} />}
    </div>
    <div className="space-y-2">{children}</div>
  </Panel>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4 py-1.5 border-b border-cc-border last:border-0">
    <span className="font-body text-sm text-cc-sub">{label}</span>
    <span className="font-body text-sm font-medium text-cc-ink text-right truncate">{value}</span>
  </div>
);

export default function System() {
  const { logout } = useAuth();
  const [mb, setMb] = useState(null);
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([
        api.get("/admin/mailboxes", { headers: authHeaders() }),
        api.get("/admin/stats", { headers: authHeaders() }),
      ]);
      setMb(m.data); setStats(s.data);
    } catch (e) { if (e?.response?.status === 401) logout(); }
  }, [logout]);

  useEffect(() => { load(); }, [load]);

  const mailOk = !!mb?.configured;
  const accounts = mb?.accounts || [];

  return (
    <AdminLayout title="Systeemmeldingen" subtitle="Status van integraties en gegevens">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <StatusCard icon={Globe} title="Website & domein" ok={true} testid="sys-domain">
          <Row label="Domein" value="vhmconstructionrenovation.be" />
          <Row label="Publieke site" value={<a href={SITE.domain} target="_blank" rel="noreferrer" className="text-cc-accent hover:underline">Openen</a>} />
          <Row label="SSL" value="Actief (HTTPS)" />
        </StatusCard>

        <StatusCard icon={Mail} title="E-mail & inbox (IMAP/SMTP)" ok={mailOk} testid="sys-mail">
          {mailOk ? (
            accounts.map((a) => <Row key={a.key} label={a.label || a.key} value={`${a.email} · ${a.unread || 0} ongelezen`} />)
          ) : (
            <p className="font-body text-sm text-cc-faint">SMTP/IMAP nog niet geconfigureerd. Transactionele mails en de inbox worden pas actief zodra de VHM-mailgegevens ingesteld zijn. Leadcapture werkt ondertussen gewoon.</p>
          )}
        </StatusCard>

        <StatusCard icon={Database} title="Database (leads)" ok={true} testid="sys-db">
          <Row label="Totaal leads" value={stats?.total ?? "—"} />
          <Row label="Nieuw" value={stats?.by_status?.nieuw ?? "—"} />
          <Row label="Bezocht" value={stats?.by_status?.bezocht ?? "—"} />
          <Row label="Offerte verzonden" value={stats?.by_status?.offerte_verzonden ?? "—"} />
          <Row label="Afgerond" value={stats?.by_status?.afgerond ?? "—"} />
        </StatusCard>

        <StatusCard icon={HardDrive} title="Bestandsopslag" ok={true} testid="sys-storage">
          <Row label="Provider" value="Emergent Object Storage (privé)" />
          <Row label="Toegang" value="Alleen admin (token)" />
        </StatusCard>

        <StatusCard icon={Users} title="Leadscore-engine" ok={true} testid="sys-scoring">
          <Row label="HOT leads" value={stats?.by_category?.hot ?? "—"} />
          <Row label="High" value={stats?.by_category?.high ?? "—"} />
          <Row label="Normaal" value={stats?.by_category?.normal ?? "—"} />
          <Row label="Laag" value={stats?.by_category?.low ?? "—"} />
        </StatusCard>
      </div>
    </AdminLayout>
  );
}
